import React from 'react';
import PropTypes from 'prop-types';

import OEToolbox from '../tools/oe-toolbox';

export const OENavigationControllerStackOperation = {
    push:   'push',
    pop:    'pop',
    remove: 'remove'
};

export class OENavigationController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.counter = 0;
        this.title = '';

        this.components = new Map();

        this.stack = [];
        this.state = {stack: this.stack.slice()};
    }

    setStateUpdate(spec)   {
        OEToolbox.updateComponentState(this, spec);
    }

    register(component, props)  {
        const id = this.counter++;
        this.components.set(id, component);

        if(!component.props.deferred) {
            this.pushInstance(id, props);
        }

        return id;
    }

    unregister(id)  {
        this.close(id);
        this.components.delete(id);
    }

    idForCompId(compId) {
        if(typeof(compId) !== 'string') return -1;
        for(const it of this.components.entries())  {
            if(it[1].props.compId === compId)   return it[0];
        }
        return -1;
    }

    compIdFromId(id)   {
        if(typeof(id) !== 'number' || id < 0) return;
        let component = this.components.get(id);
        if(!component)  return;
        return component.props.compId;
    }

    translateId(id) {
        if(typeof(id) === 'number') return id;
        if(typeof(id) === 'string') return this.idForCompId(id);
        return -1;
    }

    stackPos(id)    {
        id = this.translateId(id);
        if(id < 0)  return -1;
        return this.stack.findIndex(entry => entry.id === id);
    }

    isOnStack(id)   {
        return this.stackPos(id) >= 0;
    }

    includes(id)    {
        return this.isOnStack(id);
    }

    topId()    {
        return this.stack.length ? this.stack[this.stack.length - 1].id : -1;
    }

    topCompId()    {
        return this.compIdFromId(this.topId());
    }

    isTopId(id) {
        id = this.translateId(id);
        let topId = this.topId();
        return topId >= 0 && id >= 0 && id === topId;
    }

    top()   {
        if(this.stack.length)   return this.stack[this.stack.length - 1];
    }

    stackSize() {
        return this.stack.length;
    }

    push(compId, userData) {
        if(typeof(compId) !== 'string')     return;
        let id = this.idForCompId(compId);
        if(id < 0)  return;
        return this.pushInstance(id, undefined, userData);
    }

    pushInstance(id, props, userData) {   // creates an instance of a registered component with id and pushes it
        if(typeof(id) !== 'number') return;

        let component = this.components.get(id);
        if(!component)  return;
        return this.pushView(component.createInstance(props, userData)(id), id, userData);
    }

    pushView(view, id, userData, options) {
        id = typeof(id) === 'number' ? id : this.counter++;

        //
        if(typeof(view) === 'function') return this.pushView(view(id), id, userData);

        let oldStack = this.stack.slice();

        if(this.isOnStack(id))  {
            this.stack = this.stack.filter(entry => entry.id !== id);
        }

        this.stack.push({id: id, view: view, title: view.props.title, userData: userData});
        this.setState({stack: this.stack.slice()});
        
        this.onStackChanged(oldStack, OENavigationControllerStackOperation.push, options);
        this.onStackTopChanged(oldStack, OENavigationControllerStackOperation.push, options);

        return id;
    }

    update(id, props)	{
        let stackIndex = this.stackPos(id);
        if(stackIndex < 0)  return;

        let component = this.components.get(id);
        if(!component)  return;
        
        let view = component.createInstance(props, this.stack[stackIndex].userData)(id);
        this.stack[stackIndex] = Object.assign({}, this.stack[stackIndex], {view: view, title: view.props.title});

        this.setState({stack: this.stack.slice()});

        this.updateTitle();
    }

    updateTitle()   {
        let title;
        this.stack.forEach(entry => {
            if(entry.userData && entry.userData.title)  {
                title = entry.userData.title;
            } else if(entry.title)   {
                title = entry.title;
            }
        });

        if(title !== this.title) {
            this.title = title;
            if(this.props.onTitleChanged)    this.props.onTitleChanged(this, title);
        }
    }

    close(id, options)  {
        id = this.translateId(id);

        let stackIndex = this.stackPos(id);
        if(stackIndex < 0)  return;

        let isTopId = this.isTopId(id);

        let oldStack = this.stack.slice();

        this.stack.splice(stackIndex, 1);

        this.setState({stack: this.stack.slice()});

        this.onStackChanged(oldStack, isTopId ? OENavigationControllerStackOperation.pop : OENavigationControllerStackOperation.remove, options);
        if(isTopId) this.onStackTopChanged(oldStack, OENavigationControllerStackOperation.pop, options);
    }

    pop(id, options)   {
        if(typeof(id) === 'undefined')  {
            id = this.topId();
        } else {
            id = this.translateId(id);
        }

        let stackIndex = this.stackPos(id);
        if(stackIndex < 0)  return;

        let oldStack = this.stack.slice();

        this.stack.splice(stackIndex, this.stack.length - stackIndex);

        this.setState({stack: this.stack.slice()});

        this.onStackChanged(oldStack, OENavigationControllerStackOperation.pop, options);
        this.onStackTopChanged(oldStack, OENavigationControllerStackOperation.pop, options);
    }

    onStackTopChanged(oldStack, operation, options) {
        this.updateTitle();

        if(this.props.onStackTopChanged)    this.props.onStackTopChanged(this, oldStack, operation, options);
    }

    onStackChanged(oldStack, operation, options)    {
        if(this.props.onStackChanged)    this.props.onStackChanged(this, this.stackSize(), oldStack, operation, options);
    }

    render() {
        let viewElements = this.state.stack.map((entry, index) =>
            React.cloneElement(entry.view, {key: entry.id, onTop: index === this.state.stack.length - 1})
        );

        return (
            <div className={'navigation-controller ' + this.props.className}>
                {viewElements}
            </div>
        );
    }
}

OENavigationController.defaultProps = {
	className: ''
};

OENavigationController.propTypes = {
	className: PropTypes.string,
    onTitleChanged: PropTypes.func,
    onStackTopChanged: PropTypes.func
};

export class OENavigationView extends React.PureComponent {
    render() {
        return (
            <div className={'navigation-view ' + (this.props.onTop ? 'on-top ' : 'not-on-top ') + this.props.className}>
                {this.props.children}
            </div>
        );
    }
}

OENavigationView.defaultProps = {
    className: '',
    title: ''
};

OENavigationView.propTypes = {
    className: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

export class OENavigationViewComponent extends React.Component {

    constructor(props) {
        super(props);

        this.id = null;

        this.register();
    }

    register(props)  {
        if(this.id !== null)    return;
        props = props || this.props;
        if(!props.navigationController) return;
        this.id = props.navigationController.register(this, props);
    }

    unregister(props)    {
        if(this.id === null)    return;
        props = props || this.props;
        if(!props.navigationController) return;
        props.navigationController.unregister(this.id);
    }

    shouldComponentUpdate() {
        return false;
    }

    componentWillReceiveProps(nextProps) {
        if(this.props.navigationController !== nextProps.navigationController)  {
            this.unregister();
            this.register(nextProps);
            return;
        } else if(this.props.navigationController && this.id !== null) {
            this.props.navigationController.update(this.id, nextProps);
        }
    }

    componentWillUnmount() {
        if(!this.props.navigationController || this.id === null) return;
        
        this.props.navigationController.unregister(this.id);
        this.id = null;
    }

    createInstance(props, userData)	{
        props = props || this.props;

        const {navigationController, ref, children, ...rest} = props;

        let childrenArray = React.Children.toArray(children);

        let viewProps = {navigationController: navigationController};
        if(userData && userData.viewProps)  viewProps = Object.assign({}, viewProps, userData.viewProps);

        let childrenElements = childrenArray.map(child => React.cloneElement(child, viewProps));
        
        return ((id) =>
            <OENavigationView {...rest} id={id}>
                {childrenElements}
            </OENavigationView>
        );
    }

    render() {
        return null;
    }
}

OENavigationViewComponent.defaultProps = {
    className: '',
    title: '',
    deferred: false
};

OENavigationViewComponent.propTypes = {
    className: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    compId: PropTypes.string,
    deferred: PropTypes.bool
};

